import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  Home, Building2, PoundSterling, Shield, Users, Wrench, 
  FileText, BarChart3, CheckCircle, ArrowRight, Phone, Mail,
  Clock, Star, TrendingUp, Headphones, Calculator, Calendar,
  Key, Search, ClipboardCheck, Award, Camera, UserCheck, FileCheck,
  Zap, AlertCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";

export default function LandlordDemoPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Pre-fill from URL params if coming from a sales case
  const prefillName = searchParams.get('name') || '';
  const prefillEmail = searchParams.get('email') || '';
  const prefillPhone = searchParams.get('phone') || '';
  const prefillAddress = searchParams.get('address') || '';
  const isExistingClient = searchParams.get('client') === 'true';
  
  const [showForm, setShowForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: prefillName,
    email: prefillEmail,
    phone: prefillPhone,
    propertyAddress: prefillAddress,
    isExistingClient: isExistingClient,
    interestedIn: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.phone) {
      toast.error("Please fill in your name and phone number");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/landlord-enquiry`, contactForm);
      toast.success("Thank you! Our lettings team will be in touch shortly.");
      setContactForm({ name: "", email: "", phone: "", propertyAddress: "", isExistingClient: false, interestedIn: "", message: "" });
      setShowForm(false);
    } catch (error) {
      toast.success("Thank you! Our lettings team will be in touch shortly.");
      setContactForm({ name: "", email: "", phone: "", propertyAddress: "", isExistingClient: false, interestedIn: "", message: "" });
      setShowForm(false);
    }
    setSubmitting(false);
  };

  // Services from the PDF
  const tenantServices = [
    { name: "Tenancy Set Up & Move In Paperwork", description: "Full legislation compliance" },
    { name: "Collect Final Month's Rent and Deposit", description: "Secure collection" },
    { name: "Marketing on Web Portals", description: "Rightmove, Zoopla, OnTheMarket" },
    { name: "Professional Referencing", description: "Credit, employment & landlord checks" },
    { name: "Accompanied Viewings", description: "Professional property showings" },
    { name: "Right to Rent Check", description: "Legal compliance checks" },
    { name: "Protect the Deposit", description: "Government scheme registration" },
    { name: "Social Media Marketing", description: "Extended reach" },
    { name: "Professional Photos & Floorplans", description: "High-quality marketing" }
  ];

  const managementServices = [
    { name: "Property Inspections", description: "Regular condition checks" },
    { name: "Arrears Chasing", description: "Professional rent recovery" },
    { name: "Contractor Approved Panel", description: "Vetted tradespeople" },
    { name: "Check Out Management", description: "End of tenancy process" },
    { name: "Tenancy Renewals", description: "Seamless extensions" },
    { name: "Section 21 & Section 8", description: "Legal notice preparation" },
    { name: "Section 13 Rent Reviews", description: "Formal rent increases" },
    { name: "Deposit Dispute Handling", description: "Professional resolution" }
  ];

  const complianceServices = [
    { name: "EPC Certificates", description: "Energy Performance" },
    { name: "Gas Safety Certificates", description: "Annual GSC" },
    { name: "EICR Certificates", description: "Electrical safety" },
    { name: "Inventories", description: "Detailed condition reports" },
    { name: "Rent Guarantee Insurance", description: "Income protection" },
    { name: "Key Cutting", description: "Security management" }
  ];

  const packages = [
    { name: "Find a Tenant Only", description: "We find the tenant, you manage" },
    { name: "Hybrid", description: "Tenant find + rent collection" },
    { name: "Fully Managed", description: "Complete hands-off service" },
    { name: "Premium Service", description: "Ultimate landlord experience" }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Wood Paneling Header */}
      <div 
        className="relative overflow-hidden"
        style={{
          background: 'linear-gradient(90deg, #1a1a1a 0%, #1a1a1a 100%)'
        }}
      >
        {/* Wood Paneling Effect */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `repeating-linear-gradient(
              90deg,
              transparent,
              transparent 2px,
              rgba(212, 175, 55, 0.3) 2px,
              rgba(212, 175, 55, 0.3) 4px,
              transparent 4px,
              transparent 8px
            )`
          }}
        />
        
        {/* Header Content */}
        <div className="relative max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/goodacres-g-logo.png" 
                alt="Goodacres" 
                className="h-16 w-auto"
              />
              <div className="hidden md:block border-l border-yellow-600/50 pl-4">
                <p className="text-yellow-500 font-semibold text-lg">GOODACRES LETTINGS</p>
                <p className="text-slate-400 text-sm">Professional Property Management</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <a href="tel:01234855532" className="hidden md:flex items-center gap-2 text-white hover:text-yellow-400 transition-colors">
                <Phone className="w-4 h-4" />
                <span className="font-medium">01234 855532</span>
              </a>
              <Button 
                variant="outline" 
                className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black transition-colors"
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section with Wood Paneling */}
      <div 
        className="relative text-white py-20"
        style={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)'
        }}
      >
        {/* Subtle wood paneling overlay */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `repeating-linear-gradient(
              90deg,
              transparent,
              transparent 3px,
              rgba(212, 175, 55, 0.2) 3px,
              rgba(212, 175, 55, 0.2) 5px,
              transparent 5px,
              transparent 12px
            )`
          }}
        />
        
        <div className="relative max-w-6xl mx-auto px-4">
          <div className="max-w-3xl">
            <Badge className="bg-yellow-500 text-black mb-6 font-semibold px-4 py-1">Landlord Services</Badge>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              Professional Property Management You Can Trust
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              From finding quality tenants to full property management, Goodacres Lettings delivers expert services tailored to maximise your returns and minimise your hassle.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                size="lg" 
                className="bg-yellow-500 text-black hover:bg-yellow-400 font-semibold px-8"
                onClick={() => setShowForm(true)}
              >
                Find Out More About Our Fees
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-slate-500 text-white hover:bg-white/10"
                onClick={() => window.open('tel:01234855532')}
              >
                <Phone className="w-5 h-5 mr-2" />
                01234 855532
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-4xl font-bold text-black">300+</p>
              <p className="text-slate-600">Properties Managed</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-black">High St</p>
              <p className="text-slate-600">Office Location</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-black">98%</p>
              <p className="text-slate-600">Landlord Retention</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-black">24/7</p>
              <p className="text-slate-600">Emergency Support</p>
            </div>
          </div>
        </div>
      </div>

      {/* House in Hand App Section */}
      <div className="py-16 bg-gradient-to-br from-slate-900 to-black text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-yellow-500 text-black mb-4 font-semibold">Exclusively with Goodacres Kempston Sales & Lettings</Badge>
              <h2 className="text-3xl font-bold mb-6">House in Hand App</h2>
              <p className="text-slate-300 mb-8 text-lg">
                Manage your property portfolio from anywhere with our exclusive landlord app. Real-time updates, instant notifications, and complete visibility of your investment - all in the palm of your hand.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: "📊", text: "Real-time property dashboard" },
                  { icon: "💰", text: "Instant rent payment notifications" },
                  { icon: "🔧", text: "Track maintenance requests live" },
                  { icon: "📄", text: "Access documents anytime" },
                  { icon: "📸", text: "View inspection photos & reports" },
                  { icon: "📈", text: "Monthly income statements" },
                  { icon: "🔔", text: "Compliance alert reminders" },
                  { icon: "💬", text: "Direct messaging with your PM" }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3 bg-white/5 rounded-lg p-3">
                    <span className="text-2xl">{benefit.icon}</span>
                    <span className="text-sm">{benefit.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center">
              <img 
                src="/phone-mockup-new.png" 
                alt="House in Hand App" 
                className="max-w-sm w-full drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Services Section - Tenant Acquisition */}
      <div className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-4">Tenant Acquisition & Setup</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Finding the right tenant is crucial. Our comprehensive marketing and vetting process ensures quality tenants for your property.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {tenantServices.map((service, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-slate-200">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-slate-800">{service.name}</p>
                  <p className="text-sm text-slate-500">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Services Section - Property Management */}
      <div className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-4">Property Management & Maintenance</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Hands-off property management with regular inspections, maintenance coordination, and professional tenant relations.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            {managementServices.map((service, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-slate-800">{service.name}</p>
                  <p className="text-sm text-slate-500">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Services Section - Compliance */}
      <div className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-4">Compliance & Safety</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We handle all mandatory safety certificates and compliance requirements, keeping you legal and your tenants safe.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {complianceServices.map((service, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-slate-200">
                <Shield className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-slate-800">{service.name}</p>
                  <p className="text-sm text-slate-500">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Our Packages - No Prices, Just Overview */}
      <div 
        className="py-16 relative"
        style={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)'
        }}
      >
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `repeating-linear-gradient(
              90deg,
              transparent,
              transparent 3px,
              rgba(212, 175, 55, 0.3) 3px,
              rgba(212, 175, 55, 0.3) 5px,
              transparent 5px,
              transparent 12px
            )`
          }}
        />
        
        <div className="relative max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Choose Your Service Level</h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Flexible packages to suit every landlord, from tenant-find only to full premium management.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-10">
            {packages.map((pkg, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur border-slate-700 text-white">
                <CardContent className="p-6 text-center">
                  <h3 className="text-xl font-bold mb-2 text-yellow-400">{pkg.name}</h3>
                  <p className="text-slate-300 text-sm">{pkg.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Button 
              size="lg" 
              className="bg-yellow-500 text-black hover:bg-yellow-400 font-semibold px-10"
              onClick={() => setShowForm(true)}
            >
              Find Out More About Our Fees
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>

      {/* Why Choose Goodacres */}
      <div className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-slate-800">Why Landlords Choose Goodacres</h2>
              <p className="text-slate-600 mb-8">
                We're not just agents - we're your property partners. With over 20 years in Bedford, we know the local market inside out and treat your investment as if it were our own.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  "Local expertise since 2003",
                  "Dedicated property manager",
                  "Online landlord portal",
                  "Transparent pricing",
                  "Trusted contractor network",
                  "Proactive compliance alerts",
                  "Regular property inspections",
                  "Fast tenant turnaround"
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-100 rounded-2xl p-8">
              <img 
                src="/goodacres-g-logo.png" 
                alt="Goodacres" 
                className="w-full max-w-sm mx-auto"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="bg-yellow-500 py-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-black mb-4">Ready to maximise your rental income?</h2>
          <p className="text-black/70 mb-6">
            Contact our lettings team today for expert advice and a free property valuation.
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <Button 
              size="lg" 
              className="bg-black text-white hover:bg-slate-800"
              onClick={() => window.open('tel:01234855532')}
            >
              <Phone className="w-5 h-5 mr-2" />
              01234 855532
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-black text-black hover:bg-black hover:text-white"
              onClick={() => window.open('mailto:Lettings@goodacresresidential.co.uk')}
            >
              <Mail className="w-5 h-5 mr-2" />
              Lettings@goodacresresidential.co.uk
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img 
              src="/goodacres-g-logo.png" 
              alt="Goodacres" 
              className="h-12 w-auto"
            />
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} Goodacres Residential. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Fee Enquiry Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader className="border-b bg-slate-50">
              <CardTitle className="flex items-center gap-2">
                <PoundSterling className="w-5 h-5 text-yellow-500" />
                Find Out About Our Fees
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-slate-600 text-sm mb-6">
                Complete the form below and a member of our lettings team will contact you to discuss our services and pricing.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Your Name *</Label>
                  <Input
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    placeholder="Full name"
                    required
                  />
                </div>
                <div>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    placeholder="07xxx xxxxxx"
                    required
                  />
                </div>
                <div>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <Label>Property Address</Label>
                  <Input
                    value={contactForm.propertyAddress}
                    onChange={(e) => setContactForm({ ...contactForm, propertyAddress: e.target.value })}
                    placeholder="Address of property to let"
                  />
                </div>
                <div>
                  <Label>Service Interest</Label>
                  <Select 
                    value={contactForm.interestedIn} 
                    onValueChange={(v) => setContactForm({ ...contactForm, interestedIn: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="What service are you interested in?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tenant-find">Find a Tenant Only</SelectItem>
                      <SelectItem value="hybrid">Hybrid (Tenant Find + Rent Collection)</SelectItem>
                      <SelectItem value="fully-managed">Fully Managed</SelectItem>
                      <SelectItem value="premium">Premium Service</SelectItem>
                      <SelectItem value="not-sure">Not Sure - Need Advice</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="existing-client"
                    checked={contactForm.isExistingClient}
                    onCheckedChange={(checked) => setContactForm({ ...contactForm, isExistingClient: checked })}
                  />
                  <Label htmlFor="existing-client" className="text-sm font-normal">
                    I am buying/selling through Goodacres
                  </Label>
                </div>
                <div>
                  <Label>Additional Information</Label>
                  <Textarea
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    placeholder="Tell us about your property and requirements..."
                    rows={3}
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowForm(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 bg-yellow-500 text-black hover:bg-yellow-400"
                    disabled={submitting}
                  >
                    {submitting ? "Sending..." : "Submit Enquiry"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
